import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { useAddStaffTaskComment, useStaffTaskComments } from '@/hooks/useStaffTasks';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function TaskComments({
  taskId,
  clinicId,
}: {
  taskId: string;
  clinicId: string;
}) {
  const { t, i18n } = useTranslation();
  const comments = useStaffTaskComments(taskId);
  const addComment = useAddStaffTaskComment();
  const [body, setBody] = useState('');
  const dateLocale = i18n.language === 'da' ? da : enUS;

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    try {
      await addComment.mutateAsync({ task_id: taskId, clinic_id: clinicId, body: text });
      setBody('');
    } catch {
      toast.error(t('tasks.error_comment'));
    }
  };

  return (
    <div>
      <p className={cn(TYPE.label, 'mb-s2 text-ds-text')}>{t('tasks.comments_title')}</p>
      {comments.isLoading ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('app.loading')}</p>
      ) : (comments.data ?? []).length === 0 ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('tasks.comments_empty')}</p>
      ) : (
        <ul className="space-y-s3">
          {(comments.data ?? []).map((comment) => (
            <li key={comment.id} className="rounded-ctl bg-ds-sunk px-s3 py-s2">
              <p className={cn(TYPE.small, 'text-ds-muted')}>
                {comment.author_name}
                {' · '}
                {format(new Date(comment.created_at), 'd. MMM yyyy HH:mm', { locale: dateLocale })}
              </p>
              <p className={cn(TYPE.body, 'mt-s1 whitespace-pre-wrap text-ds-text')}>{comment.body}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-s3 space-y-s2">
        <Textarea
          id="task-comment"
          rows={2}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder={t('tasks.comment_placeholder')}
          aria-label={t('tasks.comment_placeholder')}
        />
        <Button
          type="button"
          variant="secondary"
          disabled={!body.trim() || addComment.isPending}
          loading={addComment.isPending}
          onClick={() => void submit()}
        >
          {t('tasks.comment_add')}
        </Button>
      </div>
    </div>
  );
}
